import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { ReactFlow, MiniMap, Controls, useNodesState, useEdgesState, addEdge, Connection, Edge, Node, MarkerType, useOnSelectionChange, ConnectionLineType, BaseEdge, EdgeLabelRenderer, getSmoothStepPath, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import styled, { useTheme } from 'styled-components';
import { useBlocker } from 'react-router-dom';
import { RoutingIcon, MusicIcon, RepeatIcon, ContactIcon, PhoneIcon, LogoutIcon, ScheduleIcon, LoadingIcon, MenuFoldIcon, MenuUnfoldIcon } from '../common/Icons';
import IvrNode from './IvrNode';
import IvrNodeSettings from './IvrNodeSettings';
import IvrIngressPathSettings from './IvrIngressPathSettings';
import PageState from '../common/PageState';
import KeypadModal from '../common/KeypadModal';
import { Button } from '../../styles/common/Button.styled';
import { ConsoleInput } from '../../styles/common/input.styled';
import { useApi } from '../../hooks/useApi';
import { IvrResponse, IvrType, ttsModelLabels } from '../../types/ivr';
import toast from 'react-hot-toast';
import { createGraphSnapshot, pushGraphHistory, GraphHistory, GraphSnapshot } from './ivrHistory';
import { layoutIvrGraph } from './ivrLayout';

const HISTORY_LIMIT = 50;

const EditorContainer = styled.div`
  height: calc(100dvh - 112px); min-height: 480px; display: flex; flex-direction: column;
  border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 8px; overflow: hidden;
  background: ${({ theme }) => theme.color.primary_100};
`;
const EditorShell = styled.div`position: relative; width: 100%;`;
const SaveOverlay = styled.div`
  position: absolute; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center;
  padding: 24px; background: rgba(16, 20, 28, 0.36);
`;
const SaveCard = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 12px; max-width: 420px; padding: 24px 28px;
  border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 8px;
  background: ${({ theme }) => theme.color.primary_100}; color: ${({ theme }) => theme.color.text_color_100};
  text-align: center; box-shadow: 0 20px 64px #00000024;
  strong { font-size: 15px; font-weight: 600; }
  p { margin: 0; color: ${({ theme }) => theme.color.text_color_200}; font-size: 13px; line-height: 20px; }
`;
const Toolbar = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr); align-items: center; gap: 8px; padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};
  span { font-size: 12px; color: ${({ theme }) => theme.color.text_color_300}; }
`;
const ToolbarActions = styled.div`
  display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 8px; min-width: 0;
`;
const BackButton = styled(Button).attrs({ $variant: 'secondary' })`
  justify-self: start;
  border-color: ${({ theme }) => theme.color.point_y_100};
  background: ${({ theme }) => `${theme.color.point_y_100}1A`};
  color: ${({ theme }) => theme.color.point_y_100}; font-weight: 600;
  &:hover:not(:disabled) { background: ${({ theme }) => `${theme.color.point_y_100}33`}; }
`;
const TitleField = styled.div`
  position: relative; width: 280px; min-width: 0; justify-self: center;
  label { position: absolute; right: calc(100% + 12px); top: 50%; transform: translateY(-50%); white-space: nowrap; cursor: text;
    color: ${({ theme }) => theme.color.text_color_100}; font-size: 16px; font-weight: 600; line-height: 24px; }
  input { box-sizing: border-box; width: 100%; font-size: 15px; font-weight: 600; }
`;
const PaletteToolbar = styled.div`
  position: relative; display: flex; align-items: stretch; min-height: 60px;
  border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};
`;
const PaletteToggle = styled(Button).attrs({ $variant: 'ghost' })`
  width: 200px; flex: 0 0 200px; height: auto; box-sizing: border-box;
  justify-content: space-between; padding: 0 12px; border: 0; border-right: 1px solid ${({ theme }) => theme.color.primary_300};
  border-radius: 0; background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_100}; font-weight: 600;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.color.primary_100}; }
  &:focus-visible { outline-offset: -3px; }
`;
const PaletteActions = styled.div`
  display: flex; flex: 1; min-width: 0; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; padding: 12px 16px;
  > span { font-size: 12px; color: ${({ theme }) => theme.color.text_color_300}; white-space: nowrap; }
`;
const PaletteActionButtons = styled.div`display: inline-flex; align-items: center; gap: 8px;`;
const HistoryControls = styled.div`
  position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%);
  display: inline-flex; align-items: center; gap: 6px;
  span { color: ${({ theme }) => theme.color.text_color_200}; font-size: 12px; white-space: nowrap; }
  button { width: 32px; padding: 0; }
  @media (max-width: 1199px) { position: static; transform: none; }
`;
const SaveStatus = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
`;
const UnsavedIndicator = styled.span`
  width: 7px; height: 7px; flex: 0 0 7px; border-radius: 50%;
  background: ${({ theme }) => theme.color.point_n_100};
`;
const Workspace = styled.div`display: flex; flex: 1; min-height: 0;`;
const Palette = styled.aside`
  width: 200px; flex: 0 0 200px; overflow-y: auto; padding: 12px; box-sizing: border-box;
  border-right: 1px solid ${({ theme }) => theme.color.primary_300}; background: ${({ theme }) => theme.color.primary_50};
  &[hidden] { display: none; }
  h3 { font-size: 12px; color: ${({ theme }) => theme.color.text_color_300}; margin: 4px 0 8px; }
  section + section h3 { margin-top: 16px; }
  button { width: 100%; justify-content: flex-start; margin-bottom: 6px; }
`;
const FlowWrapper = styled.div`
  flex: 1; min-width: 0; position: relative; background: ${({ theme }) => theme.color.primary_200};
  .react-flow__node.menu-return-target { outline: 3px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 5px; border-radius: 8px; box-shadow: 0 0 0 8px ${({ theme }) => `${theme.color.point_y_100}33`}; }
  .react-flow__controls { box-shadow: none; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px; overflow: hidden; }
  .react-flow__controls-button { width: 28px; height: 28px; box-sizing: border-box; background: ${({ theme }) => theme.color.primary_100}; color: ${({ theme }) => theme.color.text_color_200}; border-color: ${({ theme }) => theme.color.primary_300}; }
  .react-flow__controls-button svg { fill: currentColor; }
  .react-flow__minimap { background: ${({ theme }) => theme.color.primary_100}; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px; }
`;
const DeleteButton = styled.button`
  position: absolute; top: -10px; right: -10px; width: 28px; height: 28px; border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.color.point_n_100}; background: ${({ theme }) => theme.color.primary_100};
  color: ${({ theme }) => theme.color.point_n_100}; cursor: pointer; z-index: 100;
  font-size: 18px; line-height: 1; font-weight: 500;
  transition: background-color 140ms, border-color 140ms, color 140ms, box-shadow 140ms;

  &:hover {
    border-color: ${({ theme }) => theme.color.point_n_200};
    background: ${({ theme }) => theme.color.point_n_100};
    color: ${({ theme }) => theme.name === 'dark' ? '#101828' : '#FFFFFF'};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.color.point_n_100}26;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.point_n_100};
    outline-offset: 2px;
  }

  &:active { background: ${({ theme }) => theme.color.point_n_200}; }
`;
const IvrEdge = ({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, label, selected }: any) => {
  const theme = useTheme();
  const [path, labelX, labelY] = getSmoothStepPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition });
  const [edgeLabelX, edgeLabelY] = typeof label === 'string' && label.startsWith('번호: ') ? [targetX, targetY - 32] : [labelX, labelY];
  return <><BaseEdge path={path} markerEnd={markerEnd} style={{ strokeWidth: selected ? 3 : 2, stroke: selected ? theme.color.point_y_100 : theme.color.text_color_300 }} />
    {label && <EdgeLabelRenderer><span className="nodrag nopan" style={{ position: 'absolute', transform: `translate(-50%, -50%) translate(${edgeLabelX}px,${edgeLabelY}px)`, background: theme.color.primary_100, color: theme.color.text_color_100, border: `1px solid ${theme.color.primary_300}`, padding: '3px 7px', borderRadius: 4, fontSize: 12 }}>{label}</span></EdgeLabelRenderer>}</>;
};
const nodeTypes = { ivrNode: (props: any) => <div style={{ position: 'relative' }}><IvrNode {...props} />{props.data.ivr_type !== 'START' && <DeleteButton className="nodrag nopan" aria-label={`${props.data.custom_title || props.data.name} 삭제`} onClick={event => { event.stopPropagation(); props.data.onDelete?.(); }}>×</DeleteButton>}</div> };
const edgeTypes = { ivrEdge: IvrEdge };
const nodeIcons: Record<string, React.ComponentType<any>> = { ARS: RoutingIcon, PLAY: MusicIcon, REPEAT: RepeatIcon, MENU_RETURN: RepeatIcon, RINGING_GROUP: ContactIcon, DIRECT: PhoneIcon, HANGUP: LogoutIcon };
const nodeGroups = [
  { title: '안내', nodes: [['ARS', 'ARS (안내+분기)'], ['PLAY', '음원 재생'], ['REPEAT', '다시듣기'], ['MENU_RETURN', '상위 메뉴 이동']] },
  { title: '연결', nodes: [['RINGING_GROUP', '수신그룹'], ['DIRECT', '직접연결'], ['HANGUP', '통화 종료']] },
  { title: '조건', nodes: [['CONDITION_DAY', '요일별 분기'], ['CONDITION_TIME', '시간별 분기'], ['CONDITION_DATE', '날짜별 분기'], ['CONDITION_HOLIDAY', '공휴일 제외']] },
];
type IvrGraphEdge = Edge & { ingress_path?: string };

const formatArsEdgeLabel = (keypadNumber: string, ingressPath?: string) => `번호: ${keypadNumber}${ingressPath ? ` · ${ingressPath}` : ''}`;

const getMenuReturnTargets = (returnId: string, nodes: Node[], edges: Edge[]): string[] => {
  const types = new Map(nodes.map(node => [node.id, node.data.ivr_type]));
  const parents = new Map<string, string[]>();
  edges.forEach(edge => parents.set(edge.target, [...(parents.get(edge.target) || []), edge.source]));
  const startId = nodes.find(node => node.data.ivr_type === 'START')?.id;
  const targets = new Set<string>();
  const visited = new Set<string>();
  const stack: { id: string; lastArsFound: boolean }[] = [{ id: returnId, lastArsFound: false }];
  while (stack.length) {
    const { id, lastArsFound } = stack.pop()!;
    const key = `${id}:${lastArsFound}`;
    if (visited.has(key)) continue;
    visited.add(key);
    const isArs = types.get(id) === 'ARS';
    if (isArs && lastArsFound) { targets.add(id); continue; }
    const incoming = parents.get(id) || [];
    if (!incoming.length && startId) targets.add(startId);
    incoming.forEach(parent => stack.push({ id: parent, lastArsFound: lastArsFound || isArs }));
  }
  return Array.from(targets);
};

const HistoryArrowIcon = ({ direction }: { direction: 'left' | 'right' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {direction === 'left'
      ? <><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></>
      : <><path d="M5 12h14" /><polyline points="12 5 19 12 12 19" /></>}
  </svg>
);

const RoutingIvrEditor = ({ selectedIvr, setView, setSelectedIvr, geminiTtsKeyConfigured, onOpenTtsApiKey }: { selectedIvr: IvrResponse | null, setView: (v: 'list' | 'editor') => void, setSelectedIvr: (ivr: IvrResponse | null) => void, geminiTtsKeyConfigured?: boolean, onOpenTtsApiKey?: () => void }) => {
  const initialIvr = useRef(selectedIvr).current;
  const mounted = useRef(false);
  const saveVersion = useRef(0);
  const layoutFitFrame = useRef<number | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      saveVersion.current += 1;
      if (layoutFitFrame.current !== null) cancelAnimationFrame(layoutFitFrame.current);
    };
  }, []);
  const [detailLoading, setDetailLoading] = useState(Boolean(initialIvr?.id));
  const [detailError, setDetailError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const theme = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [compact, setCompact] = useState(() => window.innerWidth < 1440);
  useEffect(() => {
    const resize = () => setCompact(window.innerWidth < 1440);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition, fitView, getZoom, setCenter } = useReactFlow();
  const [nodes, setNodes, applyNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, applyEdgesChange] = useEdgesState<IvrGraphEdge>([]);
  const [name, setName] = useState(selectedIvr?.name || '새 IVR 설정');
  const [isDirty, setIsDirty] = useState(false);
  const isInitialLoad = useRef(true);
  const graphRef = useRef({ name, nodes, edges });
  graphRef.current = { name, nodes, edges };
  const historyRef = useRef<GraphHistory>({ past: [], future: [] });
  const [, setHistoryRevision] = useState(0);
  const nameEditingRef = useRef(false);
  const nodeDragHistoryRef = useRef(false);

  const pushHistory = useCallback(() => {
    const current = graphRef.current;
    historyRef.current = pushGraphHistory(
      historyRef.current,
      createGraphSnapshot(current.name, current.nodes, current.edges),
      HISTORY_LIMIT,
    );
    nameEditingRef.current = false;
    setHistoryRevision(revision => revision + 1);
  }, []);

  const resetHistory = useCallback(() => {
    historyRef.current = { past: [], future: [] };
    nameEditingRef.current = false;
    nodeDragHistoryRef.current = false;
    setHistoryRevision(revision => revision + 1);
  }, []);

  const [panelDraftDirty, setPanelDraftDirty] = useState(false);
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [highlightedTargetIds, setHighlightedTargetIds] = useState<string[]>([]);
  const highlightTimer = useRef<number | null>(null);
  useEffect(() => () => { if (highlightTimer.current !== null) window.clearTimeout(highlightTimer.current); }, []);
  const [activeIngressEdgeId, setActiveIngressEdgeId] = useState<string | null>(null);
  const settingsOpen = Boolean(activeNodeId || activeIngressEdgeId);
  const paletteVisible = paletteOpen && !(settingsOpen && compact);
  const settingsOpenRef = useRef(settingsOpen);
  settingsOpenRef.current = settingsOpen;
  useEffect(() => {
    if (detailLoading) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => { fitView({ padding: 0.2, maxZoom: 1 }); });
    });
    return () => cancelAnimationFrame(frame);
  }, [paletteOpen, compact, detailLoading, fitView]);
  const panelDirtyRef = useRef(false);
  const activeNodeRef = useRef<string | null>(null);
  const hasUnsavedChanges = isDirty || panelDraftDirty;
  const handlePanelDirtyChange = useCallback((dirty: boolean) => {
    panelDirtyRef.current = dirty;
    setPanelDraftDirty(dirty);
  }, []);
  const refitCanvas = useCallback(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => fitView({ padding: 0.2, maxZoom: 1 })));
  }, [fitView]);
  const closePanel = useCallback(() => {
    activeNodeRef.current = null;
    setActiveNodeId(null);
    setActiveIngressEdgeId(null);
    handlePanelDirtyChange(false);
    if (settingsOpenRef.current) refitCanvas();
  }, [handlePanelDirtyChange, refitCanvas]);
  const confirmDiscardDraft = useCallback(() => !panelDirtyRef.current || window.confirm('적용하지 않은 설정을 버리시겠습니까?'), []);
  const requestClosePanel = useCallback(() => {
    if (confirmDiscardDraft()) closePanel();
  }, [confirmDiscardDraft, closePanel]);
  const allowNodeDeletion = useCallback((ids: string[]) => {
    if (activeNodeRef.current && ids.includes(activeNodeRef.current)) {
      if (!confirmDiscardDraft()) return false;
      closePanel();
    }
    return true;
  }, [confirmDiscardDraft, closePanel]);
  const deleteNode = useCallback((id: string) => {
    if (!allowNodeDeletion([id])) return;
    const node = graphRef.current.nodes.find(item => item.id === id);
    if (!node || (node.data as any).ivr_type === 'START') return;
    pushHistory();
    setNodes(previous => previous.filter(node => node.id !== id || node.data.ivr_type === 'START'));
    setEdges(previous => previous.filter(edge => edge.source !== id && edge.target !== id));
  }, [allowNodeDeletion, pushHistory, setNodes, setEdges]);

  // Selection and measurement changes are UI state, not graph edits.
  const graphSignature = JSON.stringify({ name,
    nodes: nodes.map(({ id, type, position, data }) => ({ id, type, position, data })),
    edges: edges.map(({ selected, ...edge }) => edge),
  }, (_key, value) => typeof value === 'function' ? undefined : value);
  const currentSignature = useRef(graphSignature);
  currentSignature.current = graphSignature;
  const savedSignature = useRef<string | null>(null);
  useEffect(() => {
    if (isInitialLoad.current) return;
    setIsDirty(graphSignature !== savedSignature.current);
  }, [graphSignature]);
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  useBlocker(({ currentLocation, nextLocation }) => {
    if (hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname) {
      return !window.confirm('저장하지 않은 변경사항이 있습니다. 페이지를 벗어나시겠습니까?');
    }
    return false;
  });
  const handleBackToList = useCallback(() => {
    if (!hasUnsavedChanges || window.confirm('저장하지 않은 변경사항이 있습니다. 목록으로 이동하시겠습니까?')) setView('list');
  }, [hasUnsavedChanges, setView]);

  const [pendingConnection, setPendingConnection] = useState<Connection | null>(null);
  const [selectedElements, setSelectedElements] = useState<{ nodes: string[]; edges: string[] }>({ nodes: [], edges: [] });

  const activeIngressEdge = useMemo(() => edges.find(edge => edge.id === activeIngressEdgeId) || null, [edges, activeIngressEdgeId]);
  const disabledIngressKeys = useMemo(() => activeIngressEdge ? edges.filter(edge => edge.source === activeIngressEdge.source && edge.id !== activeIngressEdge.id).map(edge => edge.sourceHandle).filter(Boolean) as string[] : [], [activeIngressEdge, edges]);

  const usedKeys = useMemo(() => {
    if (!pendingConnection) return [];
    return edges
      .filter(edge => edge.source === pendingConnection.source)
      .map(edge => {
        // label: `번호: ${keypadNumber}` 형식에서 번호만 추출
        if (edge.sourceHandle) return edge.sourceHandle;
        const labelStr = edge.label?.toString() || '';
        const match = labelStr.match(/번호: (.*)/);
        return match ? match[1] : null;
      })
      .filter(Boolean) as string[];
  }, [edges, pendingConnection]);

  const { request: saveApi, loading: saving } = useApi<IvrResponse>();
  const { request: fetchDetail } = useApi<IvrResponse>();
  const { data: endpoints, request: fetchEndpoints } = useApi<any[]>();
  const { data: ringingGroupData, request: fetchRingingGroups } = useApi<any>();
  const { data: audioData, request: fetchAudios } = useApi<any>();
  const { request: previewTts } = useApi();
  const { request: downloadAudio } = useApi();

  const ringingGroups = (ringingGroupData?.content || (Array.isArray(ringingGroupData) ? ringingGroupData : [])) as { id: number; displayName: string }[];
  const audios = (audioData?.content || (Array.isArray(audioData) ? audioData : [])) as any[];
  const hasExternalTts = useMemo(() => nodes.some(node => {
    const data = node.data as any;
    const audioType = data.audio_type || (data.audio_file_id ? 'AUDIO' : data.tts_text ? 'TTS' : 'AUDIO');
    return (data.ivr_type === 'ARS' || data.ivr_type === 'PLAY') && audioType === 'TTS' && String(data.tts_model || '').startsWith('GEMINI');
  }), [nodes]);
  const blockingSave = saving && hasExternalTts;

  useEffect(() => {
    fetchEndpoints({ url: '/user/getPhones', method: 'GET' }).catch(console.error);
    fetchRingingGroups({ url: '/ringing-group', method: 'GET', params: { limit: 100 } }).catch(console.error);
    fetchAudios({ url: '/audio-source', method: 'GET', params: { limit: 100 } }).catch(console.error);
  }, [fetchEndpoints, fetchRingingGroups, fetchAudios]);

  useOnSelectionChange({
    onChange: ({ nodes, edges }) => {
      setSelectedElements({
        nodes: nodes.map((n) => n.id),
        edges: edges.map((e) => e.id),
      });
    },
  });

  const onLayout = useCallback(() => {
    if (nodes.length === 0) return;

    let finalPositions: ReturnType<typeof layoutIvrGraph>;
    try { finalPositions = layoutIvrGraph(nodes, edges); }
    catch (error) { toast.error(error instanceof Error ? error.message : '자동 정리에 실패했습니다.'); return; }
    const newNodes = nodes.map(n => ({
      ...n,
      position: finalPositions[n.id] || n.position,
    }));

    const changed = newNodes.some((node, index) => {
      const current = nodes[index];
      return node.position.x !== current.position.x || node.position.y !== current.position.y;
    });
    if (!changed) return;

    pushHistory();
    setNodes(newNodes);

    if (layoutFitFrame.current !== null) cancelAnimationFrame(layoutFitFrame.current);
    layoutFitFrame.current = requestAnimationFrame(() => {
      layoutFitFrame.current = requestAnimationFrame(() => {
        layoutFitFrame.current = null;
        if (mounted.current) fitView({ duration: 800, padding: 0.2, maxZoom: 1 });
      });
    });

    setIsDirty(true);
  }, [nodes, edges, pushHistory, setNodes, fitView]);

  const deleteSelected = useCallback(() => {
    const nodesToDelete = selectedElements.nodes.filter(id => nodes.find(node => node.id === id)?.data.ivr_type !== 'START');
    if (!allowNodeDeletion(nodesToDelete)) return;
    const hasStartNode = selectedElements.nodes.some(id => (nodes.find(n => n.id === id)?.data as any)?.ivr_type === 'START');

    if (hasStartNode) {
      toast.error('시작 노드는 삭제할 수 없습니다.');
    }

    const edgesToDelete = edges.filter((edge) =>
      selectedElements.edges.includes(edge.id) &&
      !nodesToDelete.includes(edge.source) &&
      !nodesToDelete.includes(edge.target)
    );
    if (nodesToDelete.length === 0 && edgesToDelete.length === 0) return;

    pushHistory();
    setNodes((nds) => nds.filter((node) => !nodesToDelete.includes(node.id) || (node.data as any).ivr_type === 'START'));
    setEdges((eds) => eds.filter((edge) =>
      !selectedElements.edges.includes(edge.id) &&
      !nodesToDelete.includes(edge.source) &&
      !nodesToDelete.includes(edge.target)
    ));
    setSelectedElements({ nodes: [], edges: [] });
  }, [selectedElements, setNodes, setEdges, nodes, edges, allowNodeDeletion, pushHistory]);

  const checkCycle = useCallback((source: string, target: string) => {
    if (source === target) return true;

    const visited = new Set<string>();
    const stack = [target];

    while (stack.length > 0) {
      const current = stack.pop()!;
      if (current === source) return true;
      if (visited.has(current)) continue;
      visited.add(current);

      const outgoingEdges = edges.filter((e) => e.source === current);
      for (const edge of outgoingEdges) {
        stack.push(edge.target);
      }
    }
    return false;
  }, [edges]);

  const focusNode = useCallback((nodeId: string) => {
    const node = graphRef.current.nodes.find(item => item.id === nodeId);
    if (!node) return;
    const width = node.measured?.width || node.width || 260;
    const height = node.measured?.height || node.height || 160;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setCenter(node.position.x + width / 2, node.position.y + height / 2, { duration: 200, zoom: Math.min(getZoom() + 0.08, 1) });
    }));
  }, [getZoom, setCenter]);

  const locateReturnTarget = useCallback((returnId: string) => {
    const { nodes: graphNodes, edges: graphEdges } = graphRef.current;
    const targetIds = getMenuReturnTargets(returnId, graphNodes, graphEdges);
    if (!targetIds.length) return;
    if (highlightTimer.current !== null) window.clearTimeout(highlightTimer.current);
    setHighlightedTargetIds(targetIds);
    if (targetIds.length === 1) focusNode(targetIds[0]);
    else fitView({ nodes: targetIds.map(id => ({ id })), padding: 0.3, maxZoom: 1, duration: 200 });
    highlightTimer.current = window.setTimeout(() => {
      setHighlightedTargetIds([]);
      highlightTimer.current = null;
    }, 2000);
  }, [fitView, focusNode]);

  const handleEditContent = useCallback((nodeId: string, _type?: string) => {
    if (activeNodeRef.current === nodeId) return;
    if (!confirmDiscardDraft()) return;
    handlePanelDirtyChange(false);
    activeNodeRef.current = nodeId;
    setActiveNodeId(nodeId);
    setActiveIngressEdgeId(null);
    focusNode(nodeId);
  }, [confirmDiscardDraft, focusNode, handlePanelDirtyChange]);

  const openIngressSettings = useCallback((edge: Edge) => {
    const sourceType = (graphRef.current.nodes.find(node => node.id === edge.source)?.data as any)?.ivr_type;
    if (sourceType !== 'ARS' || activeIngressEdgeId === edge.id) return;
    if (!confirmDiscardDraft()) return;
    handlePanelDirtyChange(false);
    activeNodeRef.current = null;
    setActiveNodeId(null);
    setActiveIngressEdgeId(edge.id);
  }, [activeIngressEdgeId, confirmDiscardDraft, handlePanelDirtyChange]);

  const handlePaneClick = useCallback(() => {
    if (settingsOpenRef.current && !panelDirtyRef.current) closePanel();
  }, [closePanel]);

  const decorateNode = useCallback((node: Node): Node => ({
    ...node,
    deletable: (node.data as any).ivr_type !== 'START',
    data: {
      ...node.data,
      onEditContent: (t: any) => handleEditContent(node.id, t),
      onDelete: (node.data as any).ivr_type === 'START' ? undefined : () => deleteNode(node.id),
    },
  }), [deleteNode, handleEditContent]);

  const decorateNodes = useCallback((graphNodes: Node[]) => graphNodes.map(decorateNode), [decorateNode]);

  const restoreSnapshot = useCallback((snapshot: GraphSnapshot) => {
    closePanel();
    setName(snapshot.name);
    setNodes(decorateNodes(snapshot.nodes));
    setEdges(snapshot.edges);
  }, [closePanel, decorateNodes, setEdges, setNodes]);

  const undo = useCallback(() => {
    if (panelDirtyRef.current || historyRef.current.past.length === 0) return;
    const current = graphRef.current;
    const previous = historyRef.current.past[historyRef.current.past.length - 1];
    historyRef.current = {
      past: historyRef.current.past.slice(0, -1),
      future: [createGraphSnapshot(current.name, current.nodes, current.edges), ...historyRef.current.future].slice(0, HISTORY_LIMIT),
    };
    nameEditingRef.current = false;
    nodeDragHistoryRef.current = false;
    restoreSnapshot(previous);
    setHistoryRevision(revision => revision + 1);
  }, [restoreSnapshot]);

  const redo = useCallback(() => {
    if (panelDirtyRef.current || historyRef.current.future.length === 0) return;
    const current = graphRef.current;
    const next = historyRef.current.future[0];
    historyRef.current = {
      past: [...historyRef.current.past, createGraphSnapshot(current.name, current.nodes, current.edges)].slice(-HISTORY_LIMIT),
      future: historyRef.current.future.slice(1),
    };
    nameEditingRef.current = false;
    nodeDragHistoryRef.current = false;
    restoreSnapshot(next);
    setHistoryRevision(revision => revision + 1);
  }, [restoreSnapshot]);

  const handleNodesChange = useCallback((changes: any[]) => {
    const hasPositionChange = changes.some(change => change.type === 'position');
    const hasRemoval = changes.some(change => change.type === 'remove');
    if (hasRemoval || (hasPositionChange && !nodeDragHistoryRef.current)) pushHistory();
    if (hasPositionChange) {
      nodeDragHistoryRef.current = !changes.some(change => change.type === 'position' && change.dragging === false);
    }
    applyNodesChange(changes);
  }, [applyNodesChange, pushHistory]);

  const handleEdgesChange = useCallback((changes: any[]) => {
    if (changes.some(change => change.type === 'remove')) pushHistory();
    applyEdgesChange(changes);
  }, [applyEdgesChange, pushHistory]);

  const handleNodeDragStop = useCallback(() => {
    nodeDragHistoryRef.current = false;
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable)) return;
      const key = event.key.toLowerCase();
      if (key === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo(); else undo();
      } else if (key === 'y' && event.ctrlKey) {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [redo, undo]);

  const handleNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!nameEditingRef.current) {
      pushHistory();
      nameEditingRef.current = true;
    }
    setName(event.target.value);
  }, [pushHistory]);

  const commitNameEdit = useCallback(() => {
    nameEditingRef.current = false;
  }, []);

  const onConnect = useCallback(
    (params: Connection) => {
      if (params.source === params.target) {
        toast.error('자기 자신에게 연결할 수 없습니다.');
        return;
      }

      if (checkCycle(params.source, params.target)) {
        toast.error('순환 참조가 발생하여 연결할 수 없습니다.');
        return;
      }

      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      const ivrType = (sourceNode?.data as any)?.ivr_type;
      const targetIvrType = (targetNode?.data as any)?.ivr_type;

      if (ivrType === 'MENU_RETURN') {
        toast.error('상위 메뉴 이동 노드에는 출력 연결을 추가할 수 없습니다.');
        return;
      }

      if (targetIvrType === 'MENU_RETURN' && edges.some(edge => edge.target === params.target)) {
        toast.error('상위 메뉴 이동 노드에는 입력 연결을 하나만 추가할 수 있습니다.');
        return;
      }

      if (targetIvrType === 'REPEAT') {
        if (ivrType !== 'ARS') {
          toast.error('다시듣기 노드는 ARS 노드 하위에만 연결될 수 있습니다.');
          return;
        }

        const alreadyHasRepeat = edges.some(edge => {
          if (edge.source !== params.source) return false;
          const tNode = nodes.find(n => n.id === edge.target);
          return (tNode?.data as any)?.ivr_type === 'REPEAT';
        });

        if (alreadyHasRepeat) {
          toast.error('ARS 노드 하위에는 하나의 다시듣기 노드만 연결할 수 있습니다.');
          return;
        }
      }

      // ARS 노드를 제외한 모든 노드의 출력 영역(Handle)은 1개의 연결만 가능
      if (ivrType !== 'ARS') {
        const existingEdge = edges.find(edge =>
          edge.source === params.source &&
          (edge.sourceHandle || null) === (params.sourceHandle || null)
        );
        if (existingEdge) {
          toast.error('해당 출력 영역은 이미 연결되어 있습니다. 하나의 연결만 가능합니다.');
          return;
        }
      }

      if (ivrType === 'ARS') {
        setPendingConnection(params);
      } else {
        const edgeId = `e${params.source}-${params.target}-${params.sourceHandle || ''}`;

        let edgeColor = theme.color.text_color_300;
        let label = '';

        if (params.sourceHandle === 'true') {
          edgeColor = theme.color.point_y_100;
          label = '참';
        } else if (params.sourceHandle === 'false') {
          edgeColor = theme.color.text_color_300;
          label = '그 외';
        } else if (ivrType === 'PLAY') {
          edgeColor = theme.color.point_y_100;
        }

        const newEdge: Edge = {
          ...params,
          id: edgeId,
          type: 'ivrEdge',
          label: label,
          labelStyle: { fill: '#fff', fontWeight: 700, fontSize: 11 },
          labelBgStyle: { fill: edgeColor, fillOpacity: 0.8, rx: 4 },
          style: { stroke: edgeColor, strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: edgeColor,
            width: 20,
            height: 20,
          },
          animated: false,
        };
        pushHistory();
        setEdges((eds) => addEdge(newEdge, eds));
      }
    },
    [nodes, edges, setEdges, checkCycle, theme, pushHistory]
  );


  const handleKeypadSelect = useCallback((keypadNumber: string, ingressPath?: string) => {
    if (pendingConnection) {
      // 이미 같은 sourceHandle(키패드 번호)을 가진 Edge가 있는지 확인
      const existingEdge = edges.find(edge =>
        edge.source === pendingConnection.source &&
        edge.sourceHandle === keypadNumber
      );

      if (existingEdge) {
        toast.error(`이미 번호 '${keypadNumber}'에 연결된 노드가 있습니다.`);
        setPendingConnection(null);
        return;
      }

      const edgeId = `e${pendingConnection.source}-${pendingConnection.target}-${keypadNumber}`;
      const normalizedIngressPath = ingressPath?.trim() || undefined;
      const newEdge: IvrGraphEdge = {
        ...pendingConnection,
        id: edgeId,
        sourceHandle: keypadNumber, // sourceHandle 명시적 추가
        type: 'ivrEdge',
        label: formatArsEdgeLabel(keypadNumber, normalizedIngressPath),
        ingress_path: normalizedIngressPath,
        labelStyle: { fill: '#fff', fontWeight: 700, fontSize: 12 },
        labelBgStyle: { fill: theme.color.primary_100, fillOpacity: 0.9, rx: 4 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: theme.color.text_color_300,
          width: 20,
          height: 20,
        },
        style: { stroke: theme.color.text_color_300, strokeWidth: 2 },
        animated: false,
      };
      pushHistory();
      setEdges((eds) => addEdge(newEdge, eds));
    }
    setPendingConnection(null);
  }, [pendingConnection, edges, setEdges, theme, pushHistory]);

  const updateIngressEdge = useCallback((edgeId: string, keypadNumber: string, ingressPath?: string) => {
    const edge = graphRef.current.edges.find(item => item.id === edgeId);
    const sourceType = (graphRef.current.nodes.find(node => node.id === edge?.source)?.data as any)?.ivr_type;
    if (!edge || sourceType !== 'ARS') return false;
    if (graphRef.current.edges.some(item => item.id !== edge.id && item.source === edge.source && item.sourceHandle === keypadNumber)) {
      toast.error(`이미 번호 '${keypadNumber}'에 연결된 노드가 있습니다.`);
      return false;
    }
    const normalizedIngressPath = ingressPath?.trim() || undefined;
    const currentIngressPath = edge.ingress_path?.trim() || undefined;
    if (edge.sourceHandle !== keypadNumber || currentIngressPath !== normalizedIngressPath) {
      pushHistory();
      setEdges(current => current.map(item => {
        if (item.id !== edge.id) return item;
        const { ingress_path: _ingressPath, ...updated } = item;
        return {
          ...updated,
          id: `e${item.source}-${item.target}-${keypadNumber}`,
          sourceHandle: keypadNumber,
          label: formatArsEdgeLabel(keypadNumber, normalizedIngressPath),
          ...(normalizedIngressPath ? { ingress_path: normalizedIngressPath } : {}),
        };
      }));
    }
    setSelectedElements({ nodes: [], edges: [] });
    closePanel();
    return true;
  }, [closePanel, pushHistory, setEdges]);

  const addNode = useCallback((type: string, position?: { x: number, y: number }) => {
    if (type !== 'START' && !nodeGroups.some(group => group.nodes.some(([nodeType]) => nodeType === type))) return;
    if (type === 'START' && nodes.some((n) => (n.data as any).ivr_type === 'START')) {
      toast.error('시작 노드는 하나만 존재할 수 있습니다.');
      return;
    }
    const id = Date.now().toString();

    let nodeName = `새 ${type} 노드`;
    switch(type) {
      case 'START': nodeName = '시작'; break;
      case 'ARS': nodeName = 'ARS 노드'; break;
      case 'PLAY': nodeName = '음원 재생'; break;
      case 'RINGING_GROUP': nodeName = '수신그룹 노드'; break;
      case 'DIRECT': nodeName = '직접연결 노드'; break;
      case 'HANGUP': nodeName = '통화 종료'; break;
      case 'CONDITION_DAY': nodeName = '요일별 분기'; break;
      case 'CONDITION_TIME': nodeName = '시간별 분기'; break;
      case 'CONDITION_DATE': nodeName = '날짜별 분기'; break;
      case 'CONDITION_HOLIDAY': nodeName = '공휴일 제외'; break;
      case 'REPEAT': nodeName = '다시듣기'; break;
      case 'MENU_RETURN': nodeName = '상위 메뉴 이동'; break;
    }

    const newNode: Node = {
      id,
      type: 'ivrNode',
      deletable: type !== 'START',
      position: position || { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
      data: {
        name: nodeName,
        ivr_type: type,
        is_enterance: type === 'START' ? 'Y' : 'N',
        repeat_count: type === 'REPEAT' ? 1 : undefined,
        return_target: type === 'MENU_RETURN' ? 'PREVIOUS' : undefined,
        condition_holiday: type === 'CONDITION_HOLIDAY' ? 'Y' : undefined,
      },
    };
    pushHistory();
    setNodes((nds) => nds.concat(decorateNode(newNode)));
  }, [decorateNode, pushHistory, setNodes, nodes]);

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }

      // get the position of the drop
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      addNode(type, position);
    },
    [screenToFlowPosition, addNode],
  );

  const updateNodeData = useCallback((nodeId: string, updates: any) => {
    const currentNode = graphRef.current.nodes.find(node => node.id === nodeId);
    if (!currentNode) return;
    const changed = Object.entries(updates).some(([key, value]) => JSON.stringify((currentNode.data as any)[key]) !== JSON.stringify(value));
    if (!changed) { closePanel(); return; }
    pushHistory();
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, ...updates } };
        }
        return node;
      })
    );
    closePanel();
  }, [setNodes, closePanel, pushHistory]);

  const saveIvr = useCallback(async () => {
    if (panelDirtyRef.current) return;
    // 유효성 검사: 노드 타입별 필수 입/출력 연결 확인
    if (nodes.length > 0) {
      const invalidNodes = nodes.filter(node => {
        const incomingEdges = edges.filter(edge => edge.target === node.id);
        const outgoingEdges = edges.filter(edge => edge.source === node.id);
        const type = (node.data as any).ivr_type as IvrType;

        if (!type) return false;

        // 1. 공통 입력 체크 (시작 노드 제외 모든 노드는 입력을 받아야 함)
        if (type !== 'START' && incomingEdges.length === 0) return true;

        // 2. 노드 타입별 출력 및 세부 조건 체크
        if (type === 'START') {
          if (outgoingEdges.length !== 1) return true; // 시작 노드는 반드시 1개의 출력이 있어야 함
        }

        if (type === 'PLAY') {
          if (outgoingEdges.length !== 1) return true; // PLAY 노드는 반드시 1개의 출력이 있어야 함
        }

        if (type === 'ARS') {
          if (outgoingEdges.length === 0) return true; // ARS는 반드시 다음 연결이 있어야 함

          const repeatChildrenCount = outgoingEdges.filter(edge => {
            const targetNode = nodes.find(n => n.id === edge.target);
            return (targetNode?.data as any).ivr_type === 'REPEAT';
          }).length;
          if (repeatChildrenCount > 1) return true;
        }

        if (type && typeof type === 'string' && type.startsWith('CONDITION')) {
          const trueEdges = outgoingEdges.filter(e => e.sourceHandle === 'true');
          const falseEdges = outgoingEdges.filter(e => e.sourceHandle === 'false');
          if (trueEdges.length !== 1 || falseEdges.length !== 1) return true; // 조건 분기는 참/그 외 모두 1개씩 연결되어야 함
        }

        // 3. 필수 값 설정 체크
        const data = node.data as any;
        if (type === 'ARS' || type === 'PLAY') {
          const audioType = data.audio_type || (data.audio_file_id ? 'AUDIO' : (data.tts_text ? 'TTS' : 'AUDIO'));
          if (audioType === 'AUDIO') {
            if (!data.audio_file_id) return true;
          } else if (audioType === 'TTS') {
            if (!data.tts_text || data.tts_text.length < 5 || data.tts_text.length > 300) return true;
            if (data.tts_model != null && !Object.prototype.hasOwnProperty.call(ttsModelLabels, data.tts_model)) return true;
          } else {
            return true;
          }
        }
        if (type === 'RINGING_GROUP') {
          if (!data.ringing_group_id) return true;
        }
        if (type === 'DIRECT') {
          if (!data.direct_number) return true;
        }
        if (type === 'CONDITION_DAY') {
          if (!data.condition_days || data.condition_days.length === 0) return true;
        }
        if (type === 'CONDITION_TIME') {
          if (!data.condition_start_time || !data.condition_end_time) return true;
        }
        if (type === 'CONDITION_DATE') {
          if (!data.condition_dates) return true;
        }
        if (type === 'CONDITION_HOLIDAY') {
          if (!data.condition_holiday) return true;
        }
        if (type === 'REPEAT') {
          if (!data.repeat_count || data.repeat_count < 1 || data.repeat_count > 3) return true;
          const incomingFromArs = incomingEdges.some(edge => {
            const srcNode = nodes.find(n => n.id === edge.source);
            return (srcNode?.data as any).ivr_type === 'ARS';
          });
          if (!incomingFromArs) return true;
        }
        if (type === 'MENU_RETURN') {
          if (incomingEdges.length !== 1 || outgoingEdges.length !== 0 || !['START', 'PREVIOUS'].includes(data.return_target)) return true;
        }

        return false;
      });

      if (invalidNodes.length > 0) {
        toast.error(`설정이 누락되었거나 연결되지 않은 노드가 ${invalidNodes.length}개 존재합니다. 모든 노드의 값 설정 및 입/출력을 연결해주세요.`);
        return;
      }
    }

    const cleanNodes = nodes.map(n => {
      const data = n.data as any;
      const audioNode = data.ivr_type === 'ARS' || data.ivr_type === 'PLAY';
      const audioType = data.audio_type || (data.audio_file_id ? 'AUDIO' : (data.tts_text ? 'TTS' : 'AUDIO'));
      return {
        ...n,
        data: Object.fromEntries(Object.entries(data).filter(([key, value]) =>
          typeof value !== 'function' && !(audioNode && (key === 'voice' || key === 'speed' || (audioType === 'AUDIO' && key === 'tts_model')))
        ))
      };
    });
    const cleanEdges = edges.map(edge => {
      const { ingress_path, ...cleanEdge } = edge;
      const ingressPath = typeof ingress_path === 'string' ? ingress_path.trim() : '';
      const isArsEdge = (nodes.find(node => node.id === edge.source)?.data as any)?.ivr_type === 'ARS';
      return isArsEdge && ingressPath ? { ...cleanEdge, ingress_path: ingressPath.slice(0, 10), label: formatArsEdgeLabel(edge.sourceHandle || '', ingressPath.slice(0, 10)) } : cleanEdge;
    });

    const ivrRequest = {
      name,
      graphVersion: selectedIvr?.graphVersion || 1,
      ivrGraphJson: {
        nodes: cleanNodes,
        edges: cleanEdges,
      },
    };

    const requestVersion = ++saveVersion.current;
    try {
      let savedIvr;
      if (selectedIvr?.id) {
        savedIvr = await saveApi({
          method: 'PUT',
          url: `/ivr/${selectedIvr.id}`,
          data: ivrRequest,
        });
      } else {
        savedIvr = await saveApi({
          method: 'POST',
          url: '/ivr',
          data: ivrRequest,
        });
      }
      if (!mounted.current || requestVersion !== saveVersion.current) return;
      savedSignature.current = graphSignature;
      toast.success('성공적으로 저장되었습니다.');
      setIsDirty(currentSignature.current !== graphSignature);
      if (savedIvr) {
        setSelectedIvr(savedIvr);
      }
    } catch (err) {
      if (!mounted.current || requestVersion !== saveVersion.current) return;
      toast.error('저장에 실패했습니다.');
    }
  }, [nodes, edges, name, selectedIvr, saveApi, setSelectedIvr, graphSignature]);

  useEffect(() => {
    const loadDetail = async () => {
      setDetailError(false);
      setDetailLoading(Boolean(initialIvr?.id));
      if (initialIvr?.id) {
        try {
          const detail = await fetchDetail({
            method: 'GET',
            url: `/ivr/${initialIvr.id}`,
          });

          if (!detail.ivrGraphJson) throw new Error('IVR graph is missing');
          if (detail.ivrGraphJson) {
            const loadedNodes = decorateNodes(detail.ivrGraphJson.nodes);
            setNodes(loadedNodes);
            setEdges((detail.ivrGraphJson.edges || []).map((e: any) => ({
              ...e,
              type: 'ivrEdge'
            })));
            savedSignature.current = JSON.stringify({ name: detail.name || initialIvr.name,
              nodes: loadedNodes.map(({ id, type, position, data }: Node) => ({ id, type, position, data })),
              edges: (detail.ivrGraphJson.edges || []).map(({ selected, ...edge }: Edge) => ({ ...edge, type: 'ivrEdge' })),
            }, (_key, value) => typeof value === 'function' ? undefined : value);
            setName(detail.name || initialIvr.name);
            resetHistory();
            isInitialLoad.current = false;
            setIsDirty(false);
          }
        } catch (err) {
          console.error('Failed to load IVR detail:', err);
          setDetailError(true);
        } finally { setDetailLoading(false); }
      } else {
        const startId = Date.now().toString();
        const initialNodes = decorateNodes([{
          id: startId,
          type: 'ivrNode',
          deletable: false,
          position: { x: 250, y: 150 },
          data: {
            name: '시작',
            ivr_type: 'START',
            is_enterance: 'Y',
          },
        }]);
        setNodes(initialNodes);
        savedSignature.current = JSON.stringify({ name: '새 IVR 설정', nodes: initialNodes.map(({ id, type, position, data }) => ({ id, type, position, data })), edges: [] }, (_key, value) => typeof value === 'function' ? undefined : value);
        isInitialLoad.current = false;
        resetHistory();
        setIsDirty(false);
      }
    };

    loadDetail();
  }, [initialIvr, loadAttempt, fetchDetail, decorateNodes, resetHistory, setNodes, setEdges]);

  const onNodesDelete = useCallback((deleted: Node[]) => {
    const deletedIds = deleted.map(n => n.id);
    setEdges((eds) => eds.filter((e) => !deletedIds.includes(e.source) && !deletedIds.includes(e.target)));
  }, [setEdges]);

  if (detailLoading) return <PageState status="loading" title="IVR 구성을 불러오는 중입니다" />;
  if (detailError) return <PageState status="error" title="IVR 구성을 불러오지 못했습니다" onRetry={() => setLoadAttempt(attempt => attempt + 1)}><BackButton onClick={handleBackToList}><HistoryArrowIcon direction="left" />목록으로</BackButton></PageState>;
  return (
    <EditorShell>
    <EditorContainer data-testid="ivr-editor-container" aria-busy={blockingSave} inert={blockingSave || undefined}>
      <Toolbar>
        <BackButton onClick={handleBackToList}><HistoryArrowIcon direction="left" />목록으로</BackButton>
        <TitleField>
          <label htmlFor="ivr-title">IVR 제목</label>
          <ConsoleInput id="ivr-title" value={name} onChange={handleNameChange} onBlur={commitNameEdit} />
        </TitleField>
        <ToolbarActions>
          <SaveStatus role="status">
            {hasUnsavedChanges && <UnsavedIndicator data-testid="unsaved-indicator" aria-hidden="true" />}
            {hasUnsavedChanges ? '변경사항 있음' : '저장됨'}
          </SaveStatus>
          {panelDraftDirty && <span id="ivr-draft-hint">설정을 적용하거나 취소하세요</span>}
          <Button $variant="primary" onClick={saveIvr} disabled={saving || panelDraftDirty} aria-describedby={panelDraftDirty ? 'ivr-draft-hint' : undefined}>{saving ? '저장 중...' : '설정 저장'}</Button>
        </ToolbarActions>
      </Toolbar>
      <PaletteToolbar>
        <PaletteToggle type="button" aria-expanded={paletteVisible} aria-controls="ivr-node-palette" onClick={() => {
          if (compact && settingsOpen) { if (!confirmDiscardDraft()) return; closePanel(); setPaletteOpen(true); }
          else setPaletteOpen(open => !open);
        }}>
          {paletteVisible ? '노드 목록 접기' : '노드 목록 펼치기'}
          <span aria-hidden="true">{paletteVisible ? <MenuFoldIcon /> : <MenuUnfoldIcon />}</span>
        </PaletteToggle>
        <PaletteActions>
          <PaletteActionButtons>
            <Button $variant="secondary" onClick={onLayout}>자동 정리</Button>
            <Button $variant="danger" onClick={deleteSelected} disabled={!selectedElements.nodes.length && !selectedElements.edges.length}>선택 삭제</Button>
          </PaletteActionButtons>
          <HistoryControls role="group" aria-label="실행 취소 및 다시 실행">
            <span aria-hidden="true">이전</span>
            <Button $variant="secondary" $size="sm" onClick={undo} disabled={panelDraftDirty || historyRef.current.past.length === 0} aria-label="실행 취소" title="실행 취소 (Ctrl/Cmd + Z)"><HistoryArrowIcon direction="left" /></Button>
            <Button $variant="secondary" $size="sm" onClick={redo} disabled={panelDraftDirty || historyRef.current.future.length === 0} aria-label="다시 실행" title="다시 실행 (Ctrl/Cmd + Shift + Z)"><HistoryArrowIcon direction="right" /></Button>
            <span aria-hidden="true">다음</span>
          </HistoryControls>
          <span>Shift + 드래그로 여러 노드 선택</span>
        </PaletteActions>
      </PaletteToolbar>
      <Workspace>
        <Palette id="ivr-node-palette" hidden={!paletteVisible} aria-label="노드 목록">{nodeGroups.map(group => <section key={group.title}><h3>{group.title}</h3>{group.nodes.map(([type, label]) => { const Icon = nodeIcons[type] || ScheduleIcon; return <Button $variant="secondary" key={type} draggable onDragStart={event => onDragStart(event, type)} onClick={() => addNode(type)}><Icon aria-hidden="true" />{label}</Button>; })}</section>)}</Palette>
        <FlowWrapper ref={reactFlowWrapper}>
          <ReactFlow nodes={highlightedTargetIds.length ? nodes.map(node => highlightedTargetIds.includes(node.id) ? { ...node, className: `${node.className || ''} menu-return-target`.trim() } : node) : nodes} edges={edges.map(edge => {
            const ingressPath = edge.ingress_path?.trim();
            const isArsEdge = (nodes.find(node => node.id === edge.source)?.data as any)?.ivr_type === 'ARS';
            return { ...edge, ...(isArsEdge && ingressPath ? { label: formatArsEdgeLabel(edge.sourceHandle || '', ingressPath) } : {}), animated: false, markerEnd: typeof edge.markerEnd === 'object' ? { ...edge.markerEnd, color: theme.color.text_color_300 } : edge.markerEnd };
          })} onNodesChange={handleNodesChange} onEdgesChange={handleEdgesChange} onNodeDragStop={handleNodeDragStop} onConnect={onConnect} onEdgeClick={(_event, edge) => openIngressSettings(edge)} onPaneClick={handlePaneClick} onNodesDelete={onNodesDelete}
            onBeforeDelete={async ({ nodes: deleted }) => allowNodeDeletion(deleted.map(node => node.id))}
            onDragOver={onDragOver} onDrop={onDrop} nodeTypes={nodeTypes} edgeTypes={edgeTypes}
            connectionLineStyle={{ stroke: theme.color.point_y_100, strokeWidth: 2 }} connectionLineType={ConnectionLineType.SmoothStep}
            defaultEdgeOptions={{ type: 'ivrEdge', style: { strokeWidth: 2 } }} fitView fitViewOptions={{ maxZoom: 1, padding: 0.2 }}>
            <Controls /><MiniMap style={{ width: 150, height: 100 }} nodeStrokeColor={theme.color.primary_300} nodeColor={theme.color.text_color_400} maskColor={theme.name === 'dark' ? '#00000033' : '#ffffff88'} />
          </ReactFlow>
        </FlowWrapper>
        {activeNodeId ? <IvrNodeSettings key={activeNodeId} nodeId={activeNodeId} nodeData={nodes.find(node => node.id === activeNodeId)?.data || {}} audios={audios} ringingGroups={ringingGroups} endpoints={endpoints || []}
          onUpdate={updateNodeData} onDirtyChange={handlePanelDirtyChange} onClose={requestClosePanel} onCancel={closePanel} previewTts={previewTts} downloadAudio={downloadAudio} geminiTtsKeyConfigured={geminiTtsKeyConfigured} onOpenTtsApiKey={onOpenTtsApiKey} onLocateReturnTarget={locateReturnTarget} />
          : activeIngressEdge && <IvrIngressPathSettings key={activeIngressEdge.id} edgeId={activeIngressEdge.id} sourceHandle={activeIngressEdge.sourceHandle} ingressPath={activeIngressEdge.ingress_path} disabledKeys={disabledIngressKeys}
            onUpdate={(key, ingressPath) => updateIngressEdge(activeIngressEdge.id, key, ingressPath)} onDirtyChange={handlePanelDirtyChange} onClose={requestClosePanel} onCancel={closePanel} />}
      </Workspace>
      <KeypadModal isOpen={Boolean(pendingConnection)} onClose={() => setPendingConnection(null)} onSelect={handleKeypadSelect} disabledKeys={usedKeys} />
    </EditorContainer>
    {blockingSave && <SaveOverlay data-testid="ivr-save-overlay" role="status" aria-label="IVR 저장 중" aria-live="polite" aria-atomic="true">
      <SaveCard>
        <LoadingIcon size={32} />
        <strong>IVR 저장 중</strong>
        <p>외부 TTS를 사용한 경우, IVR 저장 시, 일정시간이 소요됩니다. 잠시만 기다려주세요.</p>
      </SaveCard>
    </SaveOverlay>}
    </EditorShell>
  );
};
export default RoutingIvrEditor;
